/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import _ from 'lodash';
import '@culturehq/add-to-calendar/dist/styles.css';
import { bindActionCreators } from 'redux';
import { Map } from 'immutable';
import { Container } from 'react-bootstrap';
import i18n from 'i18next';
import { reactI18nextModule, withNamespaces } from 'react-i18next';

import Promise from 'bluebird';
import Loader from '~loader';

import * as componentActions from '~actions/component_actions';

import CheckoutLayout from './event_layouts/checkout';
import ListLayout from './event_layouts/list';
import MapLayoutContainer from './event_layouts/map';
import SideBySideLayout from './event_layouts/large-list';
import ColumnLayout from './event_layouts/column_layout';
import CalendarLayout from './event_layouts/calendar';
import Header from './header';
import Featured from './event_layouts/feature';
import NoEvents from './no_events';
import { applyFilters } from './search-filters/util';
import Filters from './search-filters';
import { LocaleWrapper } from '../locale-wrapper';
import ListCompressContainer from './event_layouts/list-compress';
import TicketSpotLogo from '../../static/ticket-spot-logo.png';
import { trackWidgetView } from '../utils/journey-tracking';

// new Layout imports
import DesktopList1 from './new-event-layouts/desktop/list1/list1';
import DesktopList2 from './new-event-layouts/desktop/list2/list2';
import DesktopList3 from './new-event-layouts/desktop/list3/list3';
import DesktopColumns from './new-event-layouts/desktop/columns/columns';
import DesktopMap from './new-event-layouts/desktop/map/map';
import DesktopCalendar from './new-event-layouts/desktop/calendar/calendar';
import DesktopCheckout from './new-event-layouts/desktop/checkout-only/checkout-only';
import DesktopMonthlyView from './new-event-layouts/desktop/monthly-view/monthly-view';
import ModalWithProps from './modal';
import MobileListFirstView from './new-event-layouts/mobile/list1';
import MobileListSecondView from './new-event-layouts/mobile/list2';
import MobileStackFirstView from './new-event-layouts/mobile/stack1';
import MobileStackSecondView from './new-event-layouts/mobile/stack2';

i18n
  .use(reactI18nextModule) // passes i18n down to react-i18next
  .init({
    resources: {
      en: {
        translation: {
          'Welcome to React': 'Welcome to React and react-i18next',
        },
      },
    },
    lng: 'en',
    fallbackLng: 'en',

    interpolation: {
      escapeValue: false,
    },
  });

class Widget extends Component {
  constructor() {
    super();
    this.delayedResize = _.debounce(this.resizeComponent.bind(this), 800);

    this.state = {
      initialHeight: 0,
    };
  }

  componentDidMount() {
    window.isMobileType = Wix.Utils.getDeviceType() === 'mobile';
    Wix.getSiteInfo(({ baseUrl }) => {
      window.baseUrl = baseUrl;
    });
    this.initializeAnalytics();
    this.registerEventListeners();

    // Track widget view
    trackWidgetView();

    // Hack to wait for react to finish rendering before updating the iframe
    setTimeout(() => {
      this.setState({
        initialHeight: this.updateCompHeight(),
      });
    }, 2000);
  }

  componentDidUpdate() {
    this.updateCompHeight();
  }

  updateCompHeight = () => {
    if (this.containerEl) {
      const height = this.containerEl.clientHeight;
      Wix.setHeight(height);
      window.parent.postMessage({ type: 'ticket-spot-set-height', value: height }, '*');
      return height;
    }
    return 0;
  };

  initializeAnalytics() {
    const { settings } = this.props;
    const googleMeasurementId = settings.get('googleMeasurementId');
    const facebookPixelId = settings.get('facebookPixelId');

    // Initialize Google Analytics
    if (googleMeasurementId) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${googleMeasurementId}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      // eslint-disable-next-line no-inner-declarations
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', googleMeasurementId);
    }

    // Initialize Facebook Pixel
    if (facebookPixelId) {
      // eslint-disable-next-line func-names
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        // eslint-disable-next-line func-names, no-multi-assign, no-param-reassign
        n = f.fbq = function () {
          // eslint-disable-next-line prefer-rest-params
          n.callMethod ? n.callMethod(...arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        // eslint-disable-next-line prefer-destructuring
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', facebookPixelId);
      fbq('track', 'PageView');
    }
  }

  resizeComponent = (force = false) => {
    if (this.containerEl && this.containerEl.clientHeight < this.state.initialHeight && !force) {
      Wix.setHeight(this.state.initialHeight);
    } else if (this.containerEl) {
      const height = this.containerEl.clientHeight;
      this.setState({
        initialHeight: height,
      });
      Wix.setHeight(height);
    }
  };

  registerEventListeners = () => {
    const { publish, updateSettingsState, deleteSettings } = this.props;

    Wix.addEventListener(Wix.Events.COMPONENT_DELETED, deleteSettings);

    Wix.addEventListener(Wix.Events.SITE_PUBLISHED, () => {
      publish();
    });

    window.addEventListener('resize', this.delayedResize);

    Wix.addEventListener(Wix.Events.STYLE_PARAMS_CHANGE, this.delayedResize);
    Wix.addEventListener(Wix.Events.SETTINGS_UPDATED, ({ key, value, shouldFetchEvents = false, resizeWidget = false, hardRefresh = false }) => {
      if (hardRefresh) {
        window.location.reload();
      }
      (shouldFetchEvents ? this.props.getEvents() : Promise.resolve()).then(() => {
        if (!_.isUndefined(key) && !_.isUndefined(value)) {
          updateSettingsState(key, value);
        }
        if (resizeWidget) {
          _.defer(this.updateCompHeight);
        }
      });
    });

    const onMsg = (msg) => {
      if (!process.env.DASHBOARD_CLIENT_URL.startsWith(msg.origin)) {
        return;
      }
      const { data } = msg;
      const parsedData = JSON.parse(data);

      if (parsedData.type === 'settings') {
        const { key, value, shouldFetchEvents = false, hardRefresh = false, resizeWidget = false } = parsedData;
        if (hardRefresh) {
          window.location.reload();
        }
        (shouldFetchEvents ? this.props.getEvents() : Promise.resolve()).then(() => {
          if (!_.isUndefined(key) && !_.isUndefined(value)) {
            updateSettingsState(key, value);
          }
          if (resizeWidget) {
            _.defer(this.updateCompHeight);
          }
        });
      }
    };

    window.addEventListener('message', onMsg, false);
  };

  layoutComponent(settings, isMobile) {
    const layoutDesign = settings.get('layoutDesign');
    // If desktop layout is monthly-view, don't use default mobile ListLayout
    if (isMobile && layoutDesign !== 'desktop-monthly-view') {
      return { component: ListLayout };
    }
    const mapping = {
      list: { component: ListLayout },
      listCompress: { component: ListCompressContainer },
      sideBySide: { component: SideBySideLayout },
      feature: { component: Featured },
      calendar: { component: CalendarLayout },
      vertical: { component: ColumnLayout, props: { columns: 1 } },
      twoColumns: { component: ColumnLayout, props: { columns: 2 } },
      threeColumns: { component: ColumnLayout, props: { columns: 3 } },
      checkout: { component: CheckoutLayout, props: { variant: 'default' } },
      map: { component: MapLayoutContainer },
    };
    return mapping[layoutDesign];
  }

  newLayoutComponents(settings, isMobile) {
    const layoutDesign = settings.get('layoutDesign');
    const mobileLayoutDesign = settings.get('mobileLayoutDesign');

    // If desktop layout is monthly-view, use it for mobile too since it's optimized for mobile
    let layoutKey;
    if (isMobile && layoutDesign === 'desktop-monthly-view') {
      layoutKey = layoutDesign;
    } else if (isMobile) {
      layoutKey = mobileLayoutDesign;
    } else {
      layoutKey = layoutDesign;
    }
    const componentMapping = {
      // desktop
      'desktop-list1': { component: DesktopList1 },
      'desktop-list2': { component: DesktopList2 },
      'desktop-list3': { component: DesktopList3 },
      'desktop-vertical': { component: DesktopColumns, props: { columns: 1 } },
      'desktop-two-columns': { component: DesktopColumns, props: { columns: 2 } },
      'desktop-three-columns': { component: DesktopColumns, props: { columns: 3 } },
      'desktop-map': { component: DesktopMap },
      'desktop-calendar': { component: DesktopCalendar },
      'desktop-checkout': { component: DesktopCheckout, props: { variant: 'redesign' } },
      'desktop-monthly-view': { component: DesktopMonthlyView },

      // mobile
      'mobile-list1': { component: MobileListFirstView },
      'mobile-list2': { component: MobileListSecondView },
      'mobile-stack1': { component: MobileStackFirstView },
      'mobile-stack2': { component: MobileStackSecondView },
    };
    return componentMapping[layoutKey];
  }

  render() {
    const { events, ticketSpotEvents, settings, filterEvents, filters, loading, getTicketSpotEvents } = this.props;
    const isMobile = window.isMobileType;
    const layout = this.layoutComponent(settings, isMobile) || this.newLayoutComponents(settings, isMobile);
    if (!layout) return <span />;
    const LayoutComponent = layout.component;
    const props = layout.props || {};

    const completeEvents = settings.get('allowSearchPastLimits') ? ticketSpotEvents : events;
    const filteredEvents = applyFilters(filters, filters.count() > 0 ? completeEvents : events);
    return (
      <div className={cn({ mobile: isMobile }, 'events-container')} ref={(containerEl) => (this.containerEl = containerEl)}>
        <Container fluid className="widget" ref={(w) => (this.widget = w)}>
          <LocaleWrapper>
            <Filters events={filteredEvents} settings={settings} filterEvents={filterEvents} getTicketSpotEvents={getTicketSpotEvents} />
          </LocaleWrapper>
          {settings.get('showHeader') && <Header title={settings.get('widgetTitle')} />}
          {filteredEvents.count() === 0 && !loading ? (
            <NoEvents {...this.props} />
          ) : (
            <LayoutComponent {...props} events={filteredEvents} settings={settings} isMobile={isMobile} onResizeComponent={this.delayedResize} />
          )}
        </Container>

        <ModalWithProps />
      </div>
    );
  }
}

Widget.propTypes = {
  getEvents: PropTypes.func,
  events: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  getSettings: PropTypes.instanceOf(Map).isRequired,
  updateSettingsState: PropTypes.func,
  publish: PropTypes.func,
  deleteSettings: PropTypes.func,
  isPremium: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  events: state.component.get('events'),
  ticketSpotEvents: state.component.get('ticketSpotEvents'),
  filters: state.component.get('filters'),
  settings: state.component.get('settings'),
  isPremium: state.component.getIn(['settings', 'isPremium']),
  user: state.component.get('user'),
  loading: state.component.get('loading'),
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(componentActions, dispatch);
}

function WidgetContainer(props) {
  const prom = Promise.resolve([props.getSettings, props.getEvents]);
  const fetchData = () => prom.mapSeries((asyncMethodPassed) => asyncMethodPassed());
  return (
    <>
      <Loader key="widget" fetchData={fetchData} loadingText="Loading Events" useGlobalStateLoader>
        <>
          <Widget {...props} />
          {props?.settings && ['free', 'starter', 'trial'].includes(props.settings.get('plan')) && (
            <div className="ticket-spot-powered-by">
              <a href="https://ticketspot.io" target="_blank" rel="noreferrer">
                <img src={TicketSpotLogo} alt="Powered by Ticket Spot" />
              </a>
            </div>
          )}
        </>
      </Loader>
    </>
  );
}

WidgetContainer.propTypes = {
  getSettings: PropTypes.instanceOf(Map).isRequired,
  getEvents: PropTypes.instanceOf(Map).isRequired,
  filterEvents: PropTypes.instanceOf(Map).isRequired,
};

export default withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(WidgetContainer));
